import type {
  CopilotMarkdownExtension,
  ReactComponentsExtension,
  RehypeHtmlTransformer,
  RemarkMarkdownTransformer,
} from '@github-ui/copilot-markdown/extension'
import {dataAttrToPropName, parseJsonAttribute} from '@github-ui/copilot-markdown/utils'
import React from 'react'
import {visit} from 'unist-util-visit'

import {idRawRegex, stripQuotes, typeRawRegex} from '../utils/code-block-utils'

export type NodeMeta = {
  lang?: string
  type?: string
  /** Optional id from `id=...` in the fence meta. Used to look up structured payloads attached to the message. */
  id?: string
  value: string
  isStreaming?: boolean
}

/**
 * A base class for YAML block extensions that render custom React components based on the content of fenced code blocks in markdown.
 */
export abstract class YamlBlockExtension<TProps> implements CopilotMarkdownExtension {
  abstract get dataAttribute(): `data-${string}`

  abstract isSupported(node: NodeMeta): boolean

  abstract extractProps(node: NodeMeta): TProps

  abstract createComponent(props: TProps): React.ReactNode

  get dataProperty(): string {
    return dataAttrToPropName(this.dataAttribute)
  }

  transformMarkdown: RemarkMarkdownTransformer = tree => {
    visit(tree, 'code', node => {
      const lang = node.lang
      if (!lang) return

      const type = stripQuotes(typeRawRegex.exec(node.meta ?? '')?.groups?.type ?? '')
      if (!type) return

      const idMatch = stripQuotes(idRawRegex.exec(node.meta ?? '')?.groups?.id ?? '')

      const nodeMeta: NodeMeta = {
        lang,
        type,
        id: idMatch || undefined,
        value: node.value,
        isStreaming: node.isStreaming,
      }

      if (!this.isSupported(nodeMeta)) return

      const componentProps = this.extractProps(nodeMeta)

      node.data = {
        hName: 'div',
        hProperties: {
          [this.dataProperty]: JSON.stringify(componentProps),
        },
        hChildren: [],
      }
    })
  }

  transformHtml: RehypeHtmlTransformer = tree => {
    visit(tree, 'element', (node, i, parent) => {
      const child = node.children?.[0]
      if (
        parent &&
        i != null &&
        node.tagName === 'pre' &&
        node.children?.length === 1 &&
        child?.type === 'element' &&
        this.dataProperty in child.properties
      )
        parent.children.splice(i, 1, child)
    })
  }

  reactComponents: ReactComponentsExtension = {
    div: (props, fallthrough) => {
      const componentProps = parseJsonAttribute<TProps>(props, this.dataAttribute)
      if (!componentProps) return fallthrough

      return this.createComponent(componentProps)
    },
  }
}
